// crm/src/components/modal/AddUserModal.jsx
import React, { useState, useEffect } from 'react';
import { getRoleOptions } from '../../config/roles-master';
import { usersApi } from './api/users';

const AddUserModal = ({ isOpen, onClose, onUserAdded, users = [] }) => {
  const [formData, setFormData] = useState({
    ECode: '',
    Name: '',
    Grade: '',
    Designation: '',
    Role: '',
    ReportingManagerECode: '',
    PhoneNumber: '',
    DOUCode: '',
    ZOUCode: '',
    COUCode: ''
  });
  
  const [filterOptions, setFilterOptions] = useState({
    divisions: [],
    zones: [],
    clusters: [],
    salespeople: []
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [error, setError] = useState(null);

  // Fetch filter options when filter dependencies change
  useEffect(() => {
    if (!isOpen) return;
    
    const fetchFilterOptions = async () => {
      setLoadingOptions(true);
      try {
        const options = await usersApi.getFilterOptions({
          division: formData.DOUCode,
          zone: formData.ZOUCode,
          cluster: formData.COUCode
        });
        setFilterOptions(options);
      } catch (err) {
        console.error('Error fetching filter options:', err);
        setFilterOptions({
          divisions: [],
          zones: [],
          clusters: [],
          salespeople: []
        });
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchFilterOptions();
  }, [isOpen, formData.DOUCode, formData.ZOUCode, formData.COUCode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle dependent dropdown logic
    if (name === 'DOUCode') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        ZOUCode: '', // Reset zone when division changes
        COUCode: '' // Reset cluster when division changes
      }));
    } else if (name === 'ZOUCode') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        COUCode: '' // Reset cluster when zone changes
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.ECode.trim()) {
        throw new Error('Employee Code is required');
      }
      if (!formData.Name.trim()) {
        throw new Error('Name is required');
      }
      if (!formData.Grade.trim()) {
        throw new Error('Grade is required');
      }
      if (!formData.Designation.trim()) {
        throw new Error('Designation is required');
      }
      if (!formData.Role.trim()) {
        throw new Error('Role is required');
      }
      if (!formData.PhoneNumber.trim()) {
        throw new Error('Phone number is required');
      }

      const payload = {
        ECode: formData.ECode.trim(),
        Name: formData.Name.trim(),
        Grade: formData.Grade.trim(),
        Designation: formData.Designation.trim(),
        Role: formData.Role.trim(),
        PhoneNumber: formData.PhoneNumber.trim()
      };

      // Add reporting manager only if selected
      if (formData.ReportingManagerECode.trim()) {
        payload.ReportingManagerECode = formData.ReportingManagerECode.trim();
      }

      // Add optional fields only if provided (using OU codes)
      if (formData.DOUCode.trim()) {
        payload.DOUCode = formData.DOUCode.trim();
      }
      if (formData.ZOUCode.trim()) {
        payload.ZOUCode = formData.ZOUCode.trim();
      }
      if (formData.COUCode.trim()) {
        payload.COUCode = formData.COUCode.trim();
      }

      await onUserAdded(payload);
      
      // Reset form and close modal on success
      setFormData({
        ECode: '',
        Name: '',
        Grade: '',
        Designation: '',
        Role: '',
        ReportingManagerECode: '',
        PhoneNumber: '',
        DOUCode: '',
        ZOUCode: '',
        COUCode: ''
      });
      onClose();
    } catch (err) {
      // Handle different types of errors
      let errorMessage = 'Failed to create user';
      
      if (err.message) {
        errorMessage = err.message;
      } else if (err.detail) {
        errorMessage = err.detail;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        ECode: '',
        Name: '',
        Grade: '',
        Designation: '',
        Role: '',
        ReportingManagerECode: '',
        PhoneNumber: '',
        DOUCode: '',
        ZOUCode: '',
        COUCode: ''
      });
      setError(null);
      onClose();
    }
  };

  // Get options for dropdowns
  const roleOptions = getRoleOptions();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Add New Member</h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <p className="text-sm text-red-600 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Required Fields */}
          <div className="space-y-4 mb-6">
            <h3 className="text-sm font-medium text-gray-700">Required Information</h3>
            
            <div>
              <label htmlFor="ECode" className="block text-sm font-medium text-gray-700 mb-1">
                Employee Code *
              </label>
              <input
                type="text"
                id="ECode"
                name="ECode"
                value={formData.ECode}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter employee code"
              />
            </div>

            <div>
              <label htmlFor="Name" className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <input
                type="text"
                id="Name"
                name="Name"
                value={formData.Name}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter full name"
              />
            </div>

            <div>
              <label htmlFor="Grade" className="block text-sm font-medium text-gray-700 mb-1">
                Grade *
              </label>
              <input
                type="text"
                id="Grade"
                name="Grade"
                value={formData.Grade}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter grade"
              />
            </div>

            <div>
              <label htmlFor="Designation" className="block text-sm font-medium text-gray-700 mb-1">
                Designation *
              </label>
              <input
                type="text"
                id="Designation"
                name="Designation"
                value={formData.Designation}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter designation"
              />
            </div>

            <div>
              <label htmlFor="Role" className="block text-sm font-medium text-gray-700 mb-1">
                Role *
              </label>
              <select
                id="Role"
                name="Role"
                value={formData.Role}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select a role</option>
                {roleOptions.map(role => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="PhoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number *
              </label>
              <input
                type="tel"
                id="PhoneNumber"
                name="PhoneNumber"
                value={formData.PhoneNumber}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter phone number"
              />
            </div>
          </div>

          {/* Optional Fields */}
          <div className="space-y-4 mb-6">
            <h3 className="text-sm font-medium text-gray-700">Optional Information</h3>
            
            <div>
              <label htmlFor="ReportingManagerECode" className="block text-sm font-medium text-gray-700 mb-1">
                View Access
              </label>
              <select
                id="ReportingManagerECode"
                name="ReportingManagerECode"
                value={formData.ReportingManagerECode}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Share view access</option>
                {users.map(user => (
                  <option key={user.ECode} value={user.ECode}>
                    {user.Name} ({user.ECode})
                  </option>
                ))}
              </select>
            </div>

            {/* Division Filter */}
            <div>
              <label htmlFor="DOUCode" className="block text-sm font-medium text-gray-700 mb-1">
                Division
              </label>
              <select
                id="DOUCode"
                name="DOUCode"
                value={formData.DOUCode}
                onChange={handleInputChange}
                disabled={loadingOptions}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              >
                <option value="">Select division</option>
                {filterOptions.divisions.map(division => (
                  <option key={division.code} value={division.code}>
                    {division.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Zone Filter */}
            <div>
              <label htmlFor="ZOUCode" className="block text-sm font-medium text-gray-700 mb-1">
                Zone
              </label>
              <select
                id="ZOUCode"
                name="ZOUCode"
                value={formData.ZOUCode}
                onChange={handleInputChange}
                disabled={loadingOptions || !formData.DOUCode || filterOptions.zones.length === 0}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              >
                <option value="">
                  {!formData.DOUCode ? 'Select division first' : filterOptions.zones.length === 0 ? 'No zones available' : 'Select zone'}
                </option>
                {filterOptions.zones.map(zone => (
                  <option key={zone.code} value={zone.code}>
                    {zone.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Cluster Filter */}
            <div>
              <label htmlFor="COUCode" className="block text-sm font-medium text-gray-700 mb-1">
                Cluster
              </label>
              <select
                id="COUCode"
                name="COUCode"
                value={formData.COUCode}
                onChange={handleInputChange}
                disabled={loadingOptions || !formData.DOUCode || filterOptions.clusters.length === 0}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              >
                <option value="">
                  {!formData.DOUCode ? 'Select division first' : filterOptions.clusters.length === 0 ? 'No clusters available' : 'Select cluster'}
                </option>
                {filterOptions.clusters.map(cluster => (
                  <option key={cluster.code} value={cluster.code}>
                    {cluster.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Loading indicator for filter options */}
          {loadingOptions && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center">
                <svg className="animate-spin h-4 w-4 text-blue-600 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-blue-800 text-sm">Loading organizational units...</span>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </>
              ) : (
                'Create Member'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUserModal;